import { mapMutations } from 'vuex'
import { prop, propOr, pathOr } from 'ramda'
import { EmailTemplateApi, EmailPreviewApi } from '@/services'
import useLogger from '@/composables/useLogger'
const logger = useLogger('EmailTemplateCommonMixin')

const EmailTemplateCommonMixin = {
  props: {
    emailTemplateId: {
      type: [Number, String],
      required: false,
      default: null
    }

  },
  data: function () {
    return {
      emailTemplate: {},
      showSaveDialog: false,
      showSendPreviewDialog: false,
      showTemplateSaveDialog: false,
      isSendingEmailPreview: false,
      modalError: null,
      showModalError: false,
      emailPreviewHtml: '',
      emailTemplateTemplateName: '',
      showNotice: false,
      noticeTimeout: 0,
      noticeMessage: '',
      requireTemplateHtml: false, // NOTE: subclass should override!!!
      previewFooterId: null
    }
  },
  computed: {
    emailTemplateName: {
      get: function () {
        return prop('name', this.emailTemplate) || this.suggestedName
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'name', val)
      }
    },
    emailTemplateJson: {
      get: function () {
        return propOr({}, 'template_json', this.emailTemplate)
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'template_json', val)
      }
    },
    emailTemplateHtml: {
      get: function () {
        return propOr('', 'template_html', this.emailTemplate)
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'template_html', val)
      }
    },
    isStarterTemplate: {
      get: function () {
        return propOr(false, 'is_template', this.emailTemplate)
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'is_template', val)
      }
    },
    isGlobalTemplate: {
      get: function () {
        return propOr(false, 'global_template', this.emailTemplate)
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'global_template', val)
      }
    },
    emailTemplateType: {
      get: function () {
        return pathOr(this.$options.templateType, ['type'], this.emailTemplate)
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'type', val)
      }
    },
    emailTemplateOnlyUseR4MergeTags: {
      get: function () {
        return propOr(true, ['app_only_merge_tags'], this.emailTemplate)
      },
      set: function (val) {
        this.$set(this.emailTemplate, 'app_only_merge_tags', val)
      }
    }
  },
  methods: {
    ...mapMutations('EmailTemplateStore', ['resetSuggestedName']),
    saveEmailBuilderData: function () {
      this.resetModalError()
      return EmailTemplateApi.saveEmailTemplate({
        id: this.emailTemplateId,
        dealer_id: (this.isStarterTemplate || this.isGlobalTemplate) ? null : this.dealer.id, // do not set dealer if starterTemplate
        global_template: this.isGlobalTemplate,
        name: this.emailTemplateName,
        template_json: typeof (this.emailTemplateJson) === 'object' ? JSON.stringify(this.emailTemplateJson) : this.emailTemplateJson,
        template_html: this.emailTemplateHtml,
        is_template: this.isStarterTemplate,
        type: this.emailTemplateType,
        app_only_merge_tags: this.emailTemplateOnlyUseR4MergeTags
      })
        .then(response => {
          this.showSaveDialog = false
          this.$emit('save', response)
        })
        .catch(error => {
          this.handleModalError('email', this.emailTemplateName).call(
            this,
            error
          )
        })
    },
    saveEmailTemplateData: function () {
      this.resetModalError()
      const payload = {
        name: this.emailTemplateTemplateName,
        template_json: typeof (this.emailTemplateJson) === 'object' ? JSON.stringify(this.emailTemplateJson) : this.emailTemplateJson,
        is_template: true,
        global_template: false,
        save_template_only: true,
        type: this.emailTemplateType,
        app_only_merge_tags: this.emailTemplateOnlyUseR4MergeTags
      }
      if (this.requireTemplateHtml) {
        payload.template_html = this.emailTemplateHtml
      }
      EmailTemplateApi.saveEmailTemplate(payload)
        .then(response => {
          this.showTemplateSaveDialog = false
          this.$emit('save', response)
        })
        .then(() => {
          this.doShowNotice(
            `Saved Email Template ${this.emailTemplateTemplateName}`,
            3000
          )
        })
        .catch(error => {
          this.handleModalError('template', this.emailTemplateTemplateName).call(
            this,
            error
          )
        })
    },
    handleEmailPreviewSend: function (emailPreviewSendArgs) {
      this.showSendPreviewDialog = false
      this.isSendingEmailPreview = true
      this.resetModalError()
      const recipients = emailPreviewSendArgs.recipients
      const rooftopId = emailPreviewSendArgs.dealer_rooftop_id
      EmailPreviewApi.sendTemplatePreviewEmail(recipients, this.dealer.id, rooftopId, this.emailPreviewHtml, this.previewFooterId)
        .then(() => {
          this.showSendPreviewDialog = false
          this.isSendingEmailPreview = false
          this.doShowNotice(
            'Sent Email Preview to ' + recipients.join(', '),
            4000
          )
        })
        .catch(error => {
          this.showSendPreviewDialog = false
          this.isSendingEmailPreview = false
          this.doShowNotice(`Error sending Email. ${error.message}`, 10000)
        })
    },
    handleEmailPreviewClose: function () {
      this.showSendPreviewDialog = false
    },
    handleEmailPreviewError: function (errorMessage) {
      this.modalError = errorMessage
      this.showModalError = true
    },
    // returns modal error handler
    // with params for managing various saves (template, save, etc)
    handleModalError: function (modalType, currentTemplateName) {
      return function (error) {
        logger.error('Error saving template!!!', error.message)
        if (error && error.status === 422) {
          this.modalError = error.message
          this.showModalError = true
        } else {
          this.showSaveDialog = false
          this.showTemplateSaveDialog = false
          this.doShowNotice(
            `Error Saving Email Template ${currentTemplateName}! ${error.message}`,
            60000
          )
        }
      }
    },
    resetModalError: function () {
      this.modalError = null
      this.showModalError = false
    },
    closeSaveDialog: function () {
      this.showSaveDialog = false
    },
    closeTemplateSaveDialog: function () {
      this.showTemplateSaveDialog = false
    },

    doShowNotice: function (message, timeout = 0) {
      this.noticeMessage = message
      this.noticeTimeout = timeout
      this.showNotice = true
    },
    doCloseNotice: function () {
      this.noticeMessage = ''
      this.noticeTimeout = 0
      this.showNotice = false
    }

  }
}

export default EmailTemplateCommonMixin
